import { useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import type { CollectionKey } from "@/components/usage-based-pricing-calculator/src/context";
import { UbpStep } from "@/components/usage-based-pricing-calculator/src/context";
import {
    useInputValues,
    useSetCollectionSelected,
    useStep,
    useSetStep
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { wacToken } from "@/libraries/design/foundations";

import { useIndustrySelectorTracking } from "../hooks/tracking";

import { messages } from "./messages";

const sectionStyles = css({
    paddingBlockEnd: token("space.300")
});

const titleWrapperStyles = css({
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const subtitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.250")
});

const pillContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.150")
});

const pillBaseStyles = css({
    "display": "inline-flex",
    "alignItems": "center",
    "gap": token("space.100"),
    "padding": `${token("space.075")} ${token("space.200")}`,
    "borderRadius": wacToken("corner.radius.pill"),
    "border": `1px solid ${wacToken("color.neutral.light300")}`,
    "backgroundColor": wacToken("color.neutral.light0"),
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "14px",
    "lineHeight": "20px",
    "color": wacToken("color.primary.black"),
    "cursor": "pointer",
    "transition": "all 0.15s ease",
    "&:hover": {
        borderColor: wacToken("color.primary.blue")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const pillSelectedStyles = css({
    backgroundColor: wacToken("color.extended.blue100"),
    borderColor: wacToken("color.primary.blue"),
    color: wacToken("color.primary.blue")
});

const pillIconStyles = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    flexShrink: 0
});

const learnMoreLinkStyles = css({
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "14px",
    "color": wacToken("color.primary.blue"),
    "textDecoration": "none",
    "&:hover": {
        textDecoration: "underline"
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const linkContainerStyles = css({
    marginBlockStart: token("space.150")
});

interface CollectionOption {
    id: string;
    label: string;
    icon: React.ReactNode;
    iconBg: string;
    collectionKey: CollectionKey;
}

const ConfluenceIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle cx="8" cy="8" r="6" fill="#FFFFFF" />
    </svg>
);

const JiraAlignIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <rect x="2" y="2" width="12" height="12" rx="2" fill="#FFFFFF" />
    </svg>
);

const JSMIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <polygon points="8,2 14,14 2,14" fill="#FFFFFF" />
    </svg>
);

const JiraIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle cx="8" cy="8" r="5" fill="#FFFFFF" />
    </svg>
);

export const IndustrySelector = () => {
    const { collections } = useInputValues();
    const step = useStep();
    const setCollectionSelected = useSetCollectionSelected();
    const intl = useIntl();
    const setStep = useSetStep();
    const { fireCollectionToggledEvent } = useIndustrySelectorTracking();

    const collectionOptions: CollectionOption[] = [
        {
            id: "teamwork",
            label: intl.formatMessage(messages.teamCollaboration),
            icon: <ConfluenceIcon />,
            iconBg: wacToken("color.primary.blue"),
            collectionKey: "teamwork"
        },
        {
            id: "strategy",
            label: intl.formatMessage(messages.strategyPlanning),
            icon: <JiraAlignIcon />,
            iconBg: wacToken("color.primary.purple"),
            collectionKey: "strategy"
        },
        {
            id: "service",
            label: intl.formatMessage(messages.serviceManagement),
            icon: <JSMIcon />,
            iconBg: wacToken("color.primary.yellow"),
            collectionKey: "service"
        },
        {
            id: "software",
            label: intl.formatMessage(messages.softwareDevelopment),
            icon: <JiraIcon />,
            iconBg: wacToken("color.primary.lime"),
            collectionKey: "software"
        }
    ];

    const handleToggle = useCallback(
        (option: CollectionOption) => {
            const isCurrentlySelected =
                collections[option.collectionKey].selected;
            const newSelected = !isCurrentlySelected;
            setCollectionSelected(option.collectionKey, newSelected);
            fireCollectionToggledEvent(option.collectionKey, newSelected);

            if (!isCurrentlySelected) {
                setStep(UbpStep.orgChangePercent);
            }
        },
        [
            collections,
            setCollectionSelected,
            setStep,
            fireCollectionToggledEvent
        ]
    );

    if (step < UbpStep.collections) {
        return null;
    }

    return (
        <div css={sectionStyles}>
            <div css={titleWrapperStyles}>
                <Heading size="medium" as="h2">
                    <span css={titleStyles}>
                        {intl.formatMessage(messages.industrySelectorTitle)}
                    </span>
                </Heading>
            </div>
            <p css={subtitleStyles}>
                {intl.formatMessage(messages.industrySelectorSubtitle)}
            </p>

            <div
                css={pillContainerStyles}
                role="group"
                aria-label={intl.formatMessage(messages.industrySelectorTitle)}
            >
                {collectionOptions.map((option) => {
                    const isSelected =
                        collections[option.collectionKey].selected;
                    return (
                        <button
                            key={option.id}
                            type="button"
                            css={[
                                pillBaseStyles,
                                isSelected && pillSelectedStyles
                            ]}
                            aria-pressed={isSelected}
                            onClick={() => handleToggle(option)}
                        >
                            {/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop */}
                            <span
                                css={pillIconStyles}
                                style={{ backgroundColor: option.iconBg }}
                            >
                                {option.icon}
                            </span>
                            {option.label}
                        </button>
                    );
                })}
            </div>

            <div css={linkContainerStyles}>
                <a href="/software/rovo" css={learnMoreLinkStyles}>
                    {intl.formatMessage(messages.learnMoreLink)}
                    <span aria-hidden="true"> →</span>
                </a>
            </div>
        </div>
    );
};
